import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { ArrowRight, ShieldCheck, Zap, BookOpen, CheckCircle2 } from "lucide-react";

export default function Landing() {
  const { t } = useLang();
  return (
    <Layout>
      {/* HERO */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="label-mono text-[#002FA7] mb-6">— {t("hero_kicker")}</div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              {t("hero_title")}<br />
              <span className="text-[#002FA7]">{t("hero_title2")}</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">{t("hero_sub")}</p>
            <p className="mt-3 label-mono text-slate-500">Utile per ogni concorso dell'Unione europea — in particolare <span className="text-[#0A0F1A] font-semibold">EPSO AD5 Generalist</span>.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/free"><Button size="lg" data-testid="hero-cta-start"
                className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] text-white label-mono px-8 py-6 shadow-hard-black">
                {t("hero_cta1")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button></Link>
              <Link to="/simulazione"><Button variant="outline" size="lg" data-testid="hero-cta-simulation"
                className="rounded-none border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white label-mono px-8 py-6">
                Simulazione cronometrata
              </Button></Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 label-mono text-slate-500">
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#002FA7]" />Domande originali</span>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#002FA7]" />Senza registrazione</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#002FA7]" />Correzione spiegata</span>
            </div>
            <div data-testid="landing-update-notice" className="mt-8 border-l-4 border-[#FFD700] bg-slate-50 p-5 max-w-2xl">
              <div className="label-mono text-[#002FA7] mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Aggiornato alla nuova procedura</div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Strumento di preparazione <span className="font-semibold">aggiornato alla modifica del concorso EPSO annunciata il 30 giugno 2026</span> (nuova procedura in due fasi, Fase 1 concentrata sui test di ragionamento). I materiali precedenti a questa data, reperibili su altri siti non recentemente aggiornati, rischiano di essere datati e di non riflettere il formato attuale.
              </p>
            </div>
          </div>
          <div className="lg:col-span-4 relative">
            <Link to="/free" data-testid="hero-live-pattern"
              className="block border border-slate-200 bg-white shadow-hard-black h-full min-h-[320px] p-6 group hover:-translate-y-1 transition-transform">
              <div className="flex items-center justify-between mb-4">
                <div className="label-mono text-slate-500">— Quesito numerico</div>
                <div className="label-mono text-xs bg-[#FFD700] text-[#0A0F1A] px-2 py-1">PROVA</div>
              </div>
              <p className="text-sm text-slate-700 mb-3 leading-relaxed">
                Emissioni (Mt CO₂-eq) e riduzione prevista al 2035. Quale sarà il valore atteso per <span className="font-semibold">Italia</span>?
              </p>
              <table className="w-full text-sm border border-slate-300 mb-4">
                <thead className="bg-slate-100 label-mono">
                  <tr><th className="text-left p-2 border-r border-slate-300">Stato</th><th className="text-left p-2 border-r border-slate-300">Attuali</th><th className="text-left p-2">Obiettivo</th></tr>
                </thead>
                <tbody>
                  {[["Germania", "750", "−40%"], ["Francia", "400", "−55%"], ["Italia", "410", "−30%"], ["Spagna", "270", "−45%"]].map((r, i) => (
                    <tr key={i} className={`border-t border-slate-200 ${r[0] === "Italia" ? "bg-[#002FA7]/5" : ""}`}>
                      <td className="p-2 border-r border-slate-200 font-medium">{r[0]}</td>
                      <td className="p-2 border-r border-slate-200 font-mono-num">{r[1]}</td>
                      <td className="p-2 font-mono-num">{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between">
                <span className="label-mono text-[#002FA7] group-hover:underline">Risolvi il test gratuito →</span>
                <span className="font-mono-num text-slate-400">= 287</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-slate-200 bg-slate-50/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-mono text-[#002FA7] mb-4">— {t("how_kicker")}</div>
          <h2 className="text-4xl lg:text-5xl font-black tracking-tighter mb-14 max-w-2xl">{t("how_title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Zap, t: t("how1_t"), p: t("how1_p"), n: "01" },
              { icon: BookOpen, t: t("how2_t"), p: t("how2_p"), n: "02" },
              { icon: ShieldCheck, t: t("how3_t"), p: t("how3_p"), n: "03" },
            ].map(({ icon: Icon, t: ti, p, n }) => (
              <div key={n} className="border border-slate-200 bg-white p-8 hover:shadow-hard transition-all hover:-translate-y-1">
                <div className="flex items-center justify-between mb-6">
                  <Icon className="w-8 h-8 text-[#002FA7]" strokeWidth={1.5} />
                  <span className="font-mono-num text-4xl text-slate-200 font-bold">{n}</span>
                </div>
                <h3 className="font-display font-bold text-xl mb-3">{ti}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-12">
            <div>
              <div className="label-mono text-[#002FA7] mb-4">— {t("pricing_kicker")}</div>
              <h2 className="text-4xl lg:text-5xl font-black tracking-tighter">{t("pricing_title")}</h2>
            </div>
            <p className="text-slate-600 leading-relaxed">{t("pricing_sub")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Gratuita", price: "€0", features: ["12 domande per tipo", "Senza registrazione", "Correzione con spiegazione"], cta: t("hero_cta1"), link: "/free" },
              { title: "Pro", price: "€54,90", features: ["Tutte le 450 domande con spiegazione", "Verbale · numerico · astratto", "Miglior valore"], cta: t("buy_now"), link: "/pricing" },
              { title: "Superiore", price: "€99,90", features: ["Tutte le 450 domande con spiegazione", "+ 3 guide passo-passo", "Pacchetto completo"], cta: t("buy_now"), link: "/pricing", featured: true },
            ].map((p, i) => (
              <div key={i} className={`border p-8 flex flex-col ${p.featured ? "border-[#002FA7] bg-[#0A0F1A] text-white premium-grain" : "border-slate-200 bg-white"}`}>
                <div className="label-mono opacity-70 mb-2">{p.title}</div>
                <div className="font-display font-black text-5xl mb-6">{p.price}</div>
                <ul className="space-y-2 text-sm flex-1 mb-8">
                  {p.features.map(f => (
                    <li key={f} className="flex gap-2"><CheckCircle2 className={`w-4 h-4 mt-0.5 ${p.featured ? "text-[#FFD700]" : "text-[#002FA7]"}`} />{f}</li>
                  ))}
                </ul>
                <Link to={p.link}>
                  <Button data-testid={`pricing-teaser-${i}`} className={`w-full rounded-none label-mono py-6 ${p.featured ? "bg-[#FFD700] hover:bg-white text-[#0A0F1A]" : "bg-[#002FA7] hover:bg-[#001d6b] text-white"}`}>
                    {p.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
